import { useState, useMemo } from 'react';
import { api } from '../api/client';
import aideyLogo from '../assets/sidebar-logo.svg';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';
const BORDER = '#35454C';

const inputStyle = {
    display: 'block', width: '100%', padding: '0.7rem 0.9rem',
    border: '1px solid #c5cdd4', borderRadius: 8, fontSize: '0.95rem',
    fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box',
    outline: 'none', transition: 'border-color 0.15s', background: '#fff',
};

const labelStyle = {
    display: 'block', fontSize: '0.82rem', fontWeight: 700,
    color: BORDER, marginBottom: '0.35rem', fontFamily: 'Poppins, sans-serif',
};

const errStyle = {
    background: '#fde8e8', border: '1px solid #f5a5a5', borderRadius: 8,
    padding: '0.65rem 0.9rem', fontSize: '0.85rem', color: '#A12710',
    marginBottom: '1rem', fontFamily: 'Raleway, sans-serif',
};

const okStyle = {
    background: '#eaf6ef', border: '1px solid #6abf85', borderRadius: 8,
    padding: '0.65rem 0.9rem', fontSize: '0.85rem', color: '#1e6645',
    marginBottom: '1rem', fontFamily: 'Raleway, sans-serif',
};

export default function ResetPassword() {
    const token = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('token') || '';
    }, []);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [loading, setLoading] = useState(false);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (!token) {
            setError('Reset token is missing from the URL.');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            const res = await api('/api/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify({ token, newPassword }),
            });
            const data = await res.json().catch(() => ({}));

            if (!res || !res.ok) {
                setError(data.error || 'Unable to reset password.');
            } else {
                setDone(true);
            }
        } catch {
            setError('Unable to reset password.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
            padding: '1rem', fontFamily: 'Raleway, sans-serif',
        }}>
            <div style={{
                background: '#f5f7f9',
                border: `1px solid ${BORDER}`,
                borderRadius: 16,
                width: '100%',
                maxWidth: 420,
                boxShadow: '0 12px 48px rgba(12,45,72,0.28), 0 2px 8px rgba(12,45,72,0.10)',
            }}>
                <div style={{ padding: '2rem 2rem 1.25rem', textAlign: 'center' }}>
                    <img
                        src={aideyLogo}
                        alt="Aidey"
                        style={{
                            height: 52, width: 'auto', display: 'block', margin: '0 auto',
                            border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent',
                        }}
                    />
                    <p style={{
                        margin: '0.75rem 0 0', fontSize: '0.84rem', color: '#4a5a5b',
                        fontFamily: 'Raleway, sans-serif', lineHeight: 1.4,
                    }}>
                        Set a new password
                    </p>
                </div>

                <div style={{ borderTop: '1px solid #c5cdd4', margin: '0 1.5rem' }} />

                <div style={{ padding: '1.75rem 2rem 2rem' }}>
                    <h2 style={{
                        margin: '0 0 1.25rem', fontSize: '1.15rem', color: NAVY,
                        fontFamily: 'Poppins, sans-serif', fontWeight: 700,
                    }}>
                        Reset password
                    </h2>

                    <form onSubmit={handleSubmit} noValidate>
                        {error && <div style={errStyle}>⚠ {error}</div>}
                        {done && (
                            <div style={okStyle}>
                                Password updated successfully. You can now sign in with your new password.
                            </div>
                        )}

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={labelStyle}>New password</label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="At least 8 characters"
                                style={inputStyle}
                                autoComplete="new-password"
                                required
                            />
                        </div>

                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={labelStyle}>Confirm new password</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repeat password"
                                style={inputStyle}
                                autoComplete="new-password"
                                required
                            />
                        </div>

                        <button type="submit" disabled={loading || done} style={{
                            width: '100%', padding: '0.75rem',
                            background: loading || done ? '#c8cabb' : YELLOW,
                            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '1rem',
                            fontFamily: 'Poppins, sans-serif', cursor: loading || done ? 'not-allowed' : 'pointer',
                            color: '#242629', transition: 'background 0.2s',
                        }}>
                            {loading ? 'Updating…' : 'Update password'}
                        </button>

                        <p style={{
                            textAlign: 'center', marginTop: '1.1rem', fontSize: '0.82rem',
                            color: '#4a5a5b', lineHeight: 1.6, fontFamily: 'Raleway, sans-serif',
                        }}>
                            <a href="/" style={{
                                color: NAVY, fontWeight: 700, textDecoration: 'none',
                                fontFamily: 'Raleway, sans-serif',
                            }}>
                                Back to sign in
                            </a>
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}
